import axios from 'axios';
import {
  MedicalExaminationData, MedicalActionData, PrescriptionData, SupportRequestData, ReferralData
} from './types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({ baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json' } });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const doctorApi = {
  getPatients: async (params?: any) => (await api.get('/doctor/patients', { params })).data,
  getPatientDetails: async (patientId: number, visitId?: number) => {
    return (await api.get(`/doctor/patient/${patientId}/details`, { params: { visit_id: visitId } })).data;
  },
  getPatientMedicalHistory: async (patientId: number) => {
    return (await api.get(`/doctor/patient/${patientId}/history`)).data;
  },

  storeExamination: async (data: MedicalExaminationData) => (await api.post('/doctor/examination', data)).data,
  getExamination: async (examinationId: number) => (await api.get(`/doctor/examination/${examinationId}`)).data,
  updateExamination: async (examinationId: number, data: Partial<MedicalExaminationData>) => (await api.put(`/doctor/examination/${examinationId}`, data)).data,

  getMedicalActionsCatalog: async () => (await api.get('/doctor/medical-actions/catalog')).data,
  storeMedicalActions: async (data: MedicalActionData) => (await api.post('/doctor/medical-actions', data)).data,

  getMedicationsCatalog: async (search?: string) => (await api.get('/doctor/medications/catalog', { params: { search } })).data,
  createPrescription: async (data: PrescriptionData) => (await api.post('/doctor/prescription', data)).data,
  getPrescription: async (prescriptionId: number) => (await api.get(`/doctor/prescription/${prescriptionId}`)).data,
  printPrescription: async (prescriptionId: number) => (await api.get(`/doctor/prescription/${prescriptionId}/print`, { responseType: 'blob' })).data,

  createSupportRequest: async (data: SupportRequestData) => (await api.post('/doctor/support-request', data)).data,
  getSupportRequests: async (params?: any) => (await api.get('/doctor/support-requests', { params })).data,

  createReferral: async (data: ReferralData) => (await api.post('/doctor/referral', data)).data,
  getReferral: async (referralId: number) => (await api.get(`/doctor/referral/${referralId}`)).data,
  applyDigitalSignature: async (referralId: number, signatureData: string) => (await api.post(`/doctor/referral/${referralId}/sign`, { signature: signatureData })).data,
  printReferral: async (referralId: number) => (await api.get(`/doctor/referral/${referralId}/print`, { responseType: 'blob' })).data,

  completeVisit: async (visitId: number, notes?: string) => (await api.post(`/doctor/visit/${visitId}/complete`, { notes })).data,

  getDashboardStats: async (dateRange?: { startDate: string; endDate: string }) => (await api.get('/doctor/dashboard/stats', { params: dateRange })).data,
  getRecentActivities: async (limit = 10) => (await api.get('/doctor/dashboard/activities', { params: { limit } })).data,

  searchDiagnoses: async (search: string, limit = 20) => {
    // Mock implementation
    const mockDiagnoses = [
      { code: 'J00', name: 'Acute nasopharyngitis [common cold]', category: 'Respiratory' },
      { code: 'K59.0', name: 'Constipation', category: 'Digestive' },
      { code: 'M79.3', name: 'Panniculitis, unspecified', category: 'Musculoskeletal' },
      { code: 'R50.9', name: 'Fever, unspecified', category: 'General symptoms' },
      { code: 'I10', name: 'Essential (primary) hypertension', category: 'Cardiovascular' },
      { code: 'E11.9', name: 'Type 2 diabetes mellitus without complications', category: 'Endocrine' },
      { code: 'J06.9', name: 'Acute upper respiratory infection, unspecified', category: 'Respiratory' },
      { code: 'K30', name: 'Functional dyspepsia', category: 'Digestive' },
    ];
    return Promise.resolve({
      success: true,
      data: mockDiagnoses.filter(d => d.name.toLowerCase().includes(search.toLowerCase()) || d.code.toLowerCase().includes(search.toLowerCase())).slice(0, limit)
    });
  },

  /**
   * Lookup IHS Dokter di SATUSEHAT berdasarkan No. STR
   */
  lookupDoctorIhsByStr: async (str: string) => {
    return (await api.post('/satusehat/lookup/practitioner', { str })).data;
  },

  /**
   * Lookup IHS Dokter di SATUSEHAT berdasarkan parameter STR atau NIK
   */
  lookupDoctorIhs: async (params: { str?: string; nik?: string }) => {
    return (await api.post('/satusehat/lookup/practitioner', params)).data;
  },
};
